/** Table text for the Rubric Generator's copy buttons: rows, Markdown and tab-separated. */

const PIPE_RE = /\|/g;
const TAB_RE = /\t/g;
const LINE_BREAK_RE = /\r?\n/g;

export interface RubricRowSource {
  name: string;
  points: string;
  cells: string[];
}

export interface RubricHeaders {
  criterion: string;
  points: string;
}

/** Header row (criterion, the level names, points) followed by one row per criterion. */
export function buildRubricRows(
  levels: string[],
  criteria: RubricRowSource[],
  headers: RubricHeaders
): string[][] {
  const header = [headers.criterion, ...levels, headers.points];
  const body = criteria.map((c) => [c.name, ...c.cells, c.points]);
  return [header, ...body];
}

function escapeMarkdownCell(cell: string): string {
  return cell.trim().replace(LINE_BREAK_RE, " ").replace(PIPE_RE, "\\|") || " ";
}

/** A pipe table, with the rubric title as a bold line above it when there is one. */
export function rubricToMarkdown(title: string, rows: string[][]): string {
  const toLine = (cells: string[]): string =>
    `| ${cells.map(escapeMarkdownCell).join(" | ")} |`;
  const header = toLine(rows[0] ?? []);
  const separator = `| ${(rows[0] ?? []).map(() => "---").join(" | ")} |`;
  const table = [header, separator, ...rows.slice(1).map(toLine)].join("\n");
  const heading = title.trim().replace(LINE_BREAK_RE, " ");
  return heading ? `**${heading}**\n\n${table}` : table;
}

/** Tab-separated rows, with the rubric title as the first line when there is one. */
export function rubricToTsv(title: string, rows: string[][]): string {
  const table = rows
    .map((row) =>
      row
        .map((cell) => cell.replace(TAB_RE, " ").replace(LINE_BREAK_RE, " "))
        .join("\t")
    )
    .join("\n");
  const heading = title.trim().replace(TAB_RE, " ").replace(LINE_BREAK_RE, " ");
  return heading ? `${heading}\n${table}` : table;
}
